import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Divider,
  CircularProgress,
  Grid,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { FileDownload } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import { formatLocalDateTime } from "../../utils/datetime";
import { formatAED } from "../../utils/format";
import * as XLSX from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { apiData } from "../../services/api_service";
import { computeReservationImpact } from "../../utils/reservationImpact";
import ReservationImpactTable from "../../components/ReservationImpactTable";
import CustomerUnpaidDialog from "../../components/CustomerUnpaidDialog";
import {
  fetchCreditApprovalListAPI,
  approveCreditApprovalAPI,
  rejectCreditApprovalAPI,
  creditApprovalBaseAPI,
} from "../../config";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

// Same seller block the printed PDF header uses (see INVOICE_SELLER in
// ProformaInvoiceList.jsx) — kept in sync so the preview here matches what
// generateOrderReceiptPdf / renderProformaPdf will actually print.
const DOC_SELLER = {
  companyName: "LMG G GENERAL TRADING LLC",
  addressLine: "FLEXPRO UAE BADMINTON STORE",
  cityEmirate: "Al Qusais 1, Dubai",
  poBox: "19224",
  trn: "104430108100003",
  email: "marketing@lmggt.com",
  phone: "0545699500",
};

const DOC_TITLES = {
  invoice: "CREDIT INVOICE",
  cdn: "CONSIGNMENT DELIVERY NOTE (CDN)",
  proforma: "PROFORMA INVOICE",
  cdn_convert: "CONSIGNMENT DELIVERY NOTE (CDN)",
};

// Sanity cap on Credit Days — unbounded input overflows dayjs().add() into
// "Invalid Date" wherever a Due Date is derived from it.
const MAX_CREDIT_DAYS = 3650; // 10 years

const STATUS_COLOR = {
  Pending: "warning",
  Approved: "success",
  Rejected: "error",
};

export default function CreditApprovalList() {
  const navigate = useNavigate();
  const alertRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = [295, 377].includes(Number(user?.role_id));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  // "Unpaid Balance" popup: { customerId, excludeCdnId } while open.
  const [unpaidDialog, setUnpaidDialog] = useState({ open: false, customerId: null, excludeCdnId: null });
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Edit / Approve Dialog State
  const [approveDialog, setApproveDialog] = useState({
    open: false,
    row: null,
    days: "",
    loading: false,
  });

  // Reject Dialog State
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    row: null,
    reason: "",
    loading: false,
  });

  // View dialog: the parked draft (or created document) for one request.
  const [viewDialog, setViewDialog] = useState({ open: false, loading: false, data: null, days: "" });
  // Audit trail of one request: { open, loading, row, events, legacy }
  const [historyDialog, setHistoryDialog] = useState({ open: false, loading: false, row: null, events: [], legacy: false });

  const openHistory = async (row) => {
    setHistoryDialog({ open: true, loading: true, row, events: [], legacy: false });
    const res = await apiData("get", `${creditApprovalBaseAPI}/${row.id}/history`);
    setHistoryDialog({
      open: true,
      loading: false,
      row,
      events: res?.success && res.data?.events ? res.data.events : [],
      legacy: Boolean(res?.data?.legacy),
    });
  };
  // Stock/reservation confirmation shown on top of the View dialog, right
  // before Approve actually creates the document — the same check the
  // user's own create form runs, so Admin sees it too before committing.
  const [stockConfirm, setStockConfirm] = useState({ open: false, loading: false, impact: [] });

  const openView = async (row) => {
    setViewDialog({ open: true, loading: true, data: null, days: row.requested_days || "" });
    const res = await apiData("get", `${creditApprovalBaseAPI}/${row.id}`);
    if (res?.success) {
      setViewDialog({ open: true, loading: false, data: res.data, days: res.data?.requested_days || "" });
    } else {
      setViewDialog({ open: false, loading: false, data: null, days: "" });
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: res?.msg || "Failed to load request.", messageType: "error" })
      );
    }
  };


  const fetchRequests = async () => {
    try {
      setLoading(true);
      const userId = user?.emp_id || user?.user_id || user?.id || "";
      const roleId = user?.role_id || user?.position || "";
      const res = await apiData(
        "get",
        // The API pages at 50 rows by default; ask for everything so the table, the
        // tab counts and the Excel all cover every request, not just the newest 50.
        `${fetchCreditApprovalListAPI}?status=${statusFilter}&user_id=${userId}&role_id=${roleId}&pageIndex=0&pageSize=100000`
      );
      if (res.success && res.data?.result) {
        setData(res.data.result);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching credit approvals:", err);
      alertRef.current?.triggerAlert(
        JSON.stringify({
          message: "Failed to fetch credit approval requests.",
          messageType: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  // Deep link from the header bell: /credit-approval/list?review=<id> opens
  // that request's Review dialog, then drops the param so a refresh or Close
  // doesn't reopen it.
  const location = useLocation();
  useEffect(() => {
    const reviewId = new URLSearchParams(location.search).get("review");
    if (!reviewId) return;
    openView({ id: reviewId });
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleQuickApprove = async (row) => {
    try {
      setLoading(true);
      const res = await apiData("post", approveCreditApprovalAPI, {
        requestId: row.id,
        approved_days: row.requested_days,
      });
      if (res?.success) {
        alertRef.current?.triggerAlert(
          JSON.stringify({
            message: `Credit request approved (${row.requested_days} Days) for ${row.customer_name || "Customer"}.`,
            messageType: "success",
          })
        );
        fetchRequests();
      } else {
        alertRef.current?.triggerAlert(
          JSON.stringify({ message: res?.msg || "Approval failed.", messageType: "error" })
        );
      }
    } catch (err) {
      console.error("Approve error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomApproveSubmit = async () => {
    if (!approveDialog.row || !approveDialog.days || Number(approveDialog.days) <= 0) return;
    try {
      setApproveDialog((prev) => ({ ...prev, loading: true }));
      const res = await apiData("post", approveCreditApprovalAPI, {
        requestId: approveDialog.row.id,
        approved_days: Number(approveDialog.days),
      });
      if (res?.success) {
        alertRef.current?.triggerAlert(
          JSON.stringify({
            message: `Approved ${approveDialog.days} Credit Days for ${approveDialog.row.customer_name || "Customer"}.`,
            messageType: "success",
          })
        );
        setApproveDialog({ open: false, row: null, days: "", loading: false });
        fetchRequests();
      } else {
        alertRef.current?.triggerAlert(
          JSON.stringify({ message: res?.msg || "Approval failed.", messageType: "error" })
        );
      }
    } catch (err) {
      console.error("Custom approve error:", err);
    } finally {
      setApproveDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectDialog.row) return;
    try {
      setRejectDialog((prev) => ({ ...prev, loading: true }));
      const res = await apiData("post", rejectCreditApprovalAPI, {
        requestId: rejectDialog.row.id,
        admin_notes: rejectDialog.reason,
      });
      if (res?.success) {
        alertRef.current?.triggerAlert(
          JSON.stringify({
            message: `Credit approval request for ${rejectDialog.row.customer_name || "Customer"} rejected.`,
            messageType: "info",
          })
        );
        setRejectDialog({ open: false, row: null, reason: "", loading: false });
        fetchRequests();
      } else {
        alertRef.current?.triggerAlert(
          JSON.stringify({ message: res?.msg || "Rejection failed.", messageType: "error" })
        );
      }
    } catch (err) {
      console.error("Reject error:", err);
    } finally {
      setRejectDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const DOC_LABELS = { invoice: "Invoice", cdn: "CDN", proforma: "Proforma", cdn_convert: "CDN → Invoice" };

  // Approve & Create doesn't fire the document straight away — first it
  // shows the same stock check the user's own create form runs (see
  // openSubmitConfirm in DeliveryNoteForm.jsx / Orders.jsx), computed fresh
  // against current stock, so Admin sees exactly what approving will commit
  // before it happens.
  const openStockConfirm = async () => {
    const doc = viewDialog.data?.document;
    if (!doc) return;
    setStockConfirm({ open: true, loading: true, impact: [] });
    const impact = await computeReservationImpact({
      storeId: doc.store_id,
      oldItems: [],
      newItems: (doc.items || []).map((it) => ({
        productId: it.product_id,
        quantity: it.quantity,
        product_name: it.product_name,
      })),
    });
    setStockConfirm({ open: true, loading: false, impact });
  };

  const handleViewApprove = async () => {
    const d = viewDialog.data;
    if (!d || !viewDialog.days || Number(viewDialog.days) <= 0) return;
    setStockConfirm((prev) => ({ ...prev, loading: true }));
    const res = await apiData("post", approveCreditApprovalAPI, {
      requestId: d.id,
      approved_days: Number(viewDialog.days),
    });
    if (res?.success) {
      alertRef.current?.triggerAlert(
        JSON.stringify({
          message:
            d.doc_type === "cdn_convert"
              ? `Approved ${viewDialog.days} credit days — ${d.doc_number || "the CDN"} can now be converted on Credit.`
              : `Approved ${viewDialog.days} credit days${d.document_label ? ` — ${d.document_label} created` : ""}.`,
          messageType: "success",
        })
      );
      setStockConfirm({ open: false, loading: false, impact: [] });
      setViewDialog({ open: false, loading: false, data: null, days: "" });
      fetchRequests();
    } else {
      setStockConfirm((prev) => ({ ...prev, loading: false }));
      alertRef.current?.triggerAlert(
        JSON.stringify({ message: res?.msg || "Approval failed.", messageType: "error" })
      );
    }
  };

  const handleExportExcel = () => {
    const exportData = data.map((item) => ({
      "Request ID": item.id,
      "Document": item.doc_type ? `${DOC_LABELS[item.doc_type] || item.doc_type} ${item.doc_number || "(draft)"}` : "Customer-level (legacy)",
      "Date": formatLocalDateTime(item.created_at, "DD-MM-YYYY hh:mm A"),
      "Customer Name": item.customer_name || "",
      "Company Name": item.company_name || "",
      "Customer Type": item.customer_type || "Company",
      "Requested By": item.requester_name || "",
      "Unpaid Invoices (AED)": Number(item.current_outstanding) || 0,
      "Open CDN Balance (AED)": Number(item.open_cdn_balance) || 0,
      "Open Proforma Balance (AED)": Number(item.open_proforma_balance) || 0,
      "Total Already Owed (AED)": Number(item.total_owed) || 0,
      "Document Total (AED)": item.doc_total != null ? Number(item.doc_total) : "",
      "Advance Received (AED)": Number(item.advance_received) || 0,
      "Balance on Credit (AED)": item.credit_balance != null ? Number(item.credit_balance) : "",
      "Requested Days": item.requested_days,
      "Approved Days": item.approved_days ?? "",
      "Status": item.status,
      "Reason / Notes": item.notes || "",
      "Admin Notes": item.admin_notes || "",
      "Approved By": item.approver_name || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Credit Approvals");
    writeXlsxFrozen(XLSX, wb, `Credit_Approvals_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  // Small, muted status badge (dot + label) — quieter than an outlined chip.
  const STATUS_STYLE = {
    Pending: { color: "#B45309", bg: "#FEF6E7", dot: "#F59E0B" },
    Approved: { color: "#047857", bg: "#E8F6EF", dot: "#10B981" },
    Rejected: { color: "#B42318", bg: "#FDECEA", dot: "#EF4444" },
  };
  const StatusBadge = ({ value }) => {
    const st = STATUS_STYLE[value] || { color: "#475569", bg: "#F1F5F9", dot: "#94A3B8" };
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.1,
          py: 0.35,
          borderRadius: "6px",
          bgcolor: st.bg,
          color: st.color,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: st.dot }} />
        {value}
      </Box>
    );
  };

  const goResubmit = (r) =>
    navigate(`${r.doc_type === "proforma" ? "/proforma-quote/add" : "/proforma-invoice/add"}?resubmit=${r.id}`);

  const muted = { fontSize: 12, color: "#64748B" };
  // For short fixed-format values (dates, times, ids) that must never break across lines.
  const nowrapMuted = { ...muted, whiteSpace: "nowrap !important" };

  // The customer's open (not yet invoiced) CDNs, in the CDN list.
  const openCustomerCdns = (customerId) =>
    navigate("/proforma-invoice/list", { state: { customerId, statusScope: "open" } });
  const openCustomerProformas = (customerId) =>
    navigate("/proforma-quote/list", { state: { customerId, statusScope: "open" } });

  const columns = useMemo(
    () => [
      {
        id: "request",
        header: "Request",
        size: 160,
        accessorFn: (row) => `#${row.id}`,
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#0F172A" }}>#{r.id}</Typography>
              <Typography sx={nowrapMuted}>{formatLocalDateTime(r.created_at, "DD-MM-YYYY")}</Typography>
              <Typography sx={nowrapMuted}>{formatLocalDateTime(r.created_at, "hh:mm A")}</Typography>
              {r.parent_request_id && (
                <Typography sx={{ ...nowrapMuted, color: "#B45309", mt: 0.5 }}>
                  Resubmission of #{r.parent_request_id}
                </Typography>
              )}
              {r.resubmitted_as && (
                <Typography sx={{ ...nowrapMuted, mt: 0.5 }}>Resubmitted as #{r.resubmitted_as}</Typography>
              )}
            </Box>
          );
        },
      },
      {
        id: "customer_name",
        header: "Customer",
        size: 200,
        accessorFn: (row) => `${row.company_name || ""} ${row.customer_name || ""}`.trim(),
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: "#0F172A" }}>
                {r.company_name || r.customer_name}
              </Typography>
              {r.company_name && r.customer_name && <Typography sx={muted}>Contact: {r.customer_name}</Typography>}
            </Box>
          );
        },
      },
      {
        id: "requested_by",
        header: "Requested By",
        size: 150,
        accessorFn: (row) => row.requester_name || "",
        Cell: ({ row }) => (
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
            {row.original.requester_name || "Sales Executive"}
          </Typography>
        ),
      },
      {
        id: "document",
        header: "Document",
        size: 150,
        accessorFn: (row) => (row.doc_type ? `${row.doc_type} ${row.doc_number || ""}` : ""),
        Cell: ({ row }) => {
          const r = row.original;
          if (!r.doc_type) return <Typography sx={muted}>Customer-level</Typography>;
          return (
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: "#0F172A" }}>
                {DOC_LABELS[r.doc_type] || r.doc_type}
              </Typography>
              <Typography sx={muted}>
                {r.doc_number ? r.doc_number : r.status === "Pending" ? "Draft — awaiting approval" : "Not created"}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "amounts",
        header: "Credit Amount",
        size: 230,
        enableSorting: false,
        accessorFn: (row) => row.credit_balance,
        Header: () => (
          <Tooltip
            arrow
            title="Document total, the advance already received, and the balance that would actually go on credit."
          >
            <span>Credit Amount</span>
          </Tooltip>
        ),
        Cell: ({ row }) => {
          const r = row.original;
          if (r.doc_total == null) return <Typography sx={muted}>-</Typography>;
          const adv = Number(r.advance_received) || 0;
          const line = (label, value, strong, color) => (
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Typography sx={{ ...muted, fontWeight: strong ? 700 : 400 }}>{label}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: strong ? 700 : 500, color: color || "#0F172A", whiteSpace: "nowrap" }}>
                {formatAED(value)}
              </Typography>
            </Box>
          );
          return (
            <Box sx={{ minWidth: 190 }}>
              {line("Document total", r.doc_total)}
              {adv > 0 && line("Advance received", adv, false, "#047857")}
              <Box sx={{ borderTop: "1px solid #E2E8F0", mt: 0.4, pt: 0.4 }}>
                {line("On credit", r.credit_balance, true, r.credit_balance > 0 ? "#B42318" : "#047857")}
              </Box>
            </Box>
          );
        },
      },
      {
        id: "days",
        header: "Credit Days",
        size: 120,
        accessorFn: (row) => row.requested_days,
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                {r.requested_days} days
              </Typography>
              <Typography sx={muted}>
                {r.approved_days ? `Approved: ${r.approved_days} days` : "requested"}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => <StatusBadge value={cell.getValue() || "Pending"} />,
      },
      {
        id: "decision",
        header: "Decision",
        size: 210,
        accessorFn: (row) => row.approver_name || "",
        Cell: ({ row }) => {
          const r = row.original;
          const decided = r.status !== "Pending";
          return (
            <Box>
              {decided ? (
                <>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{r.approver_name || "-"}</Typography>
                  <Typography sx={nowrapMuted}>{formatLocalDateTime(r.updated_at, "DD-MM-YYYY")}</Typography>
                  <Typography sx={nowrapMuted}>{formatLocalDateTime(r.updated_at, "hh:mm A")}</Typography>
                  {r.admin_notes && (
                    <Typography
                      sx={{
                        ...muted,
                        mt: 0.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      title={r.admin_notes}
                    >
                      “{r.admin_notes}”
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={muted}>Awaiting decision</Typography>
              )}
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={(e) => {
                  e.stopPropagation();
                  openHistory(r);
                }}
                sx={{ fontSize: 12, mt: 0.25 }}
              >
                View history
              </Link>
            </Box>
          );
        },
      },
      {
        id: "actions",
        header: "",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const r = row.original;
          const isPending = r.status === "Pending";
          // A per-document request (doc_type set): the draft, Approve & Create
          // and Reject all live in the View dialog, so the row only needs one
          // button into it. A legacy customer-level request keeps compact
          // approve/reject icons here.
          if (r.doc_type) {
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, alignItems: "flex-start" }}>
                <Button
                  size="small"
                  variant={isPending && isAdmin ? "contained" : "outlined"}
                  color={isPending && isAdmin ? "primary" : "inherit"}
                  startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                  sx={{ textTransform: "none", fontSize: 12.5, py: 0.35, px: 1.5, borderRadius: "8px", minWidth: 118 }}
                  onClick={() => openView(r)}
                >
                  {isPending && isAdmin ? "Review" : "View"}
                </Button>
                {r.can_resubmit && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<ReplayOutlinedIcon fontSize="small" />}
                    sx={{ textTransform: "none", fontSize: 12.5, py: 0.35, px: 1.5, borderRadius: "8px", minWidth: 118 }}
                    onClick={() => goResubmit(r)}
                  >
                    Edit &amp; resubmit
                  </Button>
                )}
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              {isPending && isAdmin ? (
                <>
                  <Tooltip title={`Approve ${r.requested_days} Days`}>
                    <IconButton size="small" onClick={() => handleQuickApprove(r)}>
                      <CheckCircleOutlineIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit days & approve">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setApproveDialog({ open: true, row: r, days: r.requested_days || "", loading: false })
                      }
                    >
                      <EditOutlinedIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reject Request">
                    <IconButton
                      size="small"
                      onClick={() => setRejectDialog({ open: true, row: r, reason: "", loading: false })}
                    >
                      <HighlightOffIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  —
                </Typography>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin]
  );

  // Counts for the tab labels, from whatever the current load returned.
  const countBy = (s) => data.filter((r) => r.status === s).length;

  return (
    <Layout>
      <AlertMessage ref={alertRef} />
      <Box sx={{ pt: "85px", px: { xs: 2, md: 3.5 }, pb: 4 }}>
        {/* Breadcrumb & Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2.5 }}>
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.75, fontSize: 13 }}>
              <Link color="inherit" underline="hover" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary" sx={{ fontSize: 13 }}>
                Credit Approvals
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}>Credit Terms Approvals</Typography>
            <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.25 }}>
              Requests for credit payment terms on CDNs, Proformas and Invoices.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FileDownload />}
            onClick={handleExportExcel}
            sx={{ textTransform: "none", borderRadius: "8px", borderColor: "#CBD5E1", color: "#334155" }}
          >
            Export Excel
          </Button>
        </Box>

        {/* Status tabs + table in one clean card */}
        <Paper
          elevation={0}
          sx={{ border: "1px solid #E2E8F0", borderRadius: "12px", overflow: "hidden", bgcolor: "#fff" }}
        >
          <Tabs
            value={statusFilter}
            onChange={(e, val) => setStatusFilter(val)}
            sx={{ borderBottom: "1px solid #E2E8F0", px: 1.5, minHeight: 46 }}
          >
            {[
              ["All Requests", "All"],
              ["Pending", "Pending"],
              ["Approved", "Approved"],
              ["Rejected", "Rejected"],
            ].map(([label, value]) => (
              <Tab
                key={value}
                value={value}
                label={
                  statusFilter === value && value !== "All" && !loading
                    ? `${label} (${countBy(value)})`
                    : label
                }
                sx={{ textTransform: "none", fontWeight: 600, fontSize: 13.5, minHeight: 46 }}
              />
            ))}
          </Tabs>

          <MaterialReactTable
            columns={columns}
            data={data}
            state={{ isLoading: loading }}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableSorting
            enableGlobalFilter
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enablePagination
            enableStickyHeader
            initialState={{
              density: "comfortable",
              pagination: { pageIndex: 0, pageSize: 10 },
              showGlobalFilter: true,
            }}
            muiSearchTextFieldProps={{
              placeholder: "Search customer, document, requester…",
              size: "small",
              variant: "outlined",
              sx: { minWidth: 300 },
            }}
            muiTopToolbarProps={{ sx: { px: 2, py: 1, bgcolor: "#fff", minHeight: 56 } }}
            muiBottomToolbarProps={{ sx: { borderTop: "1px solid #E2E8F0", bgcolor: "#fff" } }}
            muiPaginationProps={{ rowsPerPageOptions: [10, 25, 50] }}
            muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 0, boxShadow: "none" } }}
            muiTableContainerProps={{ sx: { maxHeight: "calc(100vh - 330px)", minHeight: 360 } }}
            layoutMode="grid"
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F8FAFC !important",
                color: "#475569 !important",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                border: "0 !important",
                borderBottom: "1px solid #E2E8F0 !important",
                py: 1.25,
                "& svg": { color: "#94A3B8 !important" },
                "& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active": { color: "#475569 !important" },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                py: 1.4,
                border: "0 !important",
                borderBottom: "1px solid #EEF2F6 !important",
                verticalAlign: "top",
                // This table's cells are multi-line (name + date + note), so
                // opt out of the app-wide 52px single-line row clamp.
                height: "auto !important",
                maxHeight: "none !important",
                whiteSpace: "normal !important",
                overflow: "visible !important",
                cursor: "default !important",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                backgroundColor: "#fff !important",
                "& td": { backgroundColor: "#fff !important" },
                "&:hover td": { backgroundColor: "#F8FAFC !important" },
              },
            }}
            displayColumnDefOptions={{ "mrt-row-actions": { size: 0 } }}
            renderEmptyRowsFallback={() => (
              <Typography sx={{ textAlign: "center", py: 6, color: "#94A3B8", fontSize: 14 }}>
                No credit requests found.
              </Typography>
            )}
          />
        </Paper>
      </Box>

      <CustomerUnpaidDialog
        open={unpaidDialog.open}
        customerId={unpaidDialog.customerId}
        excludeCdnId={unpaidDialog.excludeCdnId}
        onClose={() => setUnpaidDialog((d) => ({ ...d, open: false }))}
        onViewCdns={() => openCustomerCdns(unpaidDialog.customerId)}
        onViewProformas={() => openCustomerProformas(unpaidDialog.customerId)}
      />

      {/* Edit & Approve Custom Days Dialog */}
      <Dialog
        open={approveDialog.open}
        onClose={() => setApproveDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Credit Days</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2">
              Customer: <strong>{approveDialog.row?.customer_name || approveDialog.row?.company_name}</strong>
            </Typography>
            <Typography variant="body2">
              Requested By: <strong>{approveDialog.row?.requester_name || "Sales Exec"}</strong> ({approveDialog.row?.requested_days} Days)
            </Typography>
            <TextField
              label="Approved Credit Days"
              type="number"
              fullWidth
              value={approveDialog.days}
              onChange={(e) => {
                // Cap at 10 years — an unbounded value overflows dayjs
                // Due Date math into "Invalid Date" elsewhere.
                let v = e.target.value;
                if (v && Number(v) > MAX_CREDIT_DAYS) v = String(MAX_CREDIT_DAYS);
                setApproveDialog((prev) => ({ ...prev, days: v }));
              }}
              inputProps={{ min: 1, max: MAX_CREDIT_DAYS }}
              placeholder="e.g. 7, 10, 15, 30"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApproveDialog((prev) => ({ ...prev, open: false }))} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!approveDialog.days || Number(approveDialog.days) <= 0 || approveDialog.loading}
            onClick={handleCustomApproveSubmit}
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Request Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#e74c3c" }}>Reject Credit Request</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2">
              Customer: <strong>{rejectDialog.row?.customer_name || rejectDialog.row?.company_name}</strong>
            </Typography>
            <Typography variant="body2">
              Requested Days: <strong>{rejectDialog.row?.requested_days} Days</strong>
            </Typography>
            <TextField
              label="Rejection Reason / Admin Notes"
              multiline
              rows={3}
              fullWidth
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Enter reason for rejecting this credit request..."
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog((prev) => ({ ...prev, open: false }))} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectDialog.loading}
            onClick={handleRejectSubmit}
          >
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* View a per-document request: an exact mockup of the PDF that will be
          generated (or was generated, once approved), plus — while Pending —
          the approve/reject decision right below it. */}
      <Dialog
        open={viewDialog.open}
        onClose={() => setViewDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#f5f6f8", borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              Credit request #{viewDialog.data?.id}
            </Typography>
            {viewDialog.data && (
              <Typography sx={{ fontSize: 12.5, color: "#64748B", mt: 0.25 }}>
                {DOC_LABELS[viewDialog.data.doc_type] || viewDialog.data.doc_type}
                {viewDialog.data.doc_number ? ` · ${viewDialog.data.doc_number}` : " · draft, created on approval"}
              </Typography>
            )}
          </Box>
          {viewDialog.data && <StatusBadge value={viewDialog.data.status} />}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#f5f6f8" }}>
          {viewDialog.loading && !viewDialog.data ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : viewDialog.data ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Summary: who/what is asked, and the money — side by side */}
              {(() => {
                const dd = viewDialog.data;
                const doc = dd.document || {};
                const total = doc.grand_total != null ? Number(doc.grand_total) : null;
                const adv = Number(doc.advance_received) || 0;
                const label = { fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, color: "#64748B", textTransform: "uppercase", mb: 0.75 };
                const row = (k, v, strong) => (
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.3 }}>
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>{k}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: strong ? 700 : 500, color: "#0F172A", textAlign: "right" }}>{v}</Typography>
                  </Box>
                );
                const card = { bgcolor: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", p: 2 };
                return (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...card, height: "100%" }}>
                        <Typography sx={label}>Request</Typography>
                        {row("Customer", dd.company_name || dd.customer_name || "-", true)}
                        {dd.company_name && dd.customer_name && row("Contact", dd.customer_name)}
                        {row("Requested by", dd.requester_name || "-")}
                        {row("Payment type", String(doc.payment_type) === "2" ? "Immediate payment" : "Credit payment terms", true)}
                        {String(doc.payment_type) === "2" &&
                          row("Payment method", doc.advance_methods || "-")}
                        {row("Credit days requested", `${dd.requested_days} days`, true)}
                        {dd.notes && (
                          <Typography sx={{ fontSize: 12.5, color: "#475569", fontStyle: "italic", mt: 0.75 }}>
                            “{dd.notes}”
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...card, height: "100%" }}>
                        <Typography sx={label}>Credit amount</Typography>
                        {total != null ? (
                          <>
                            {row("Document total", `${formatAED(total)}`)}
                            {adv > 0 &&
                              row(
                                doc.advance_methods ? `Advance received (${doc.advance_methods})` : "Advance received",
                                `${formatAED(adv)}`
                              )}
                            <Box sx={{ borderTop: "1px solid #E2E8F0", mt: 0.75, pt: 0.75, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>To go on credit</Typography>
                              <Typography sx={{ fontSize: 18, fontWeight: 700, color: Math.max(0, total - adv) > 0 ? "#B42318" : "#047857" }}>
                                {formatAED(Math.max(0, total - adv))}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                            Credit on an existing CDN — see the CDN below.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()}

              {/* The customer's existing unpaid balance, with the way into the detail */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #E2E8F0",
                  borderLeft: `4px solid ${Number(viewDialog.data.total_owed) > 0 ? "#F59E0B" : "#10B981"}`,
                  borderRadius: "10px",
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, color: "#64748B", textTransform: "uppercase" }}>
                    Customer's existing balance
                  </Typography>
                  {Number(viewDialog.data.total_owed) > 0 ? (
                    <>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#B45309", mt: 0.25 }}>
                        {formatAED(viewDialog.data.total_owed)} already owed
                      </Typography>
                      <Typography sx={muted}>
                        Unpaid invoices {formatAED(viewDialog.data.current_outstanding)}
                        {" · "}
                        {viewDialog.data.open_cdn_count} CDN{Number(viewDialog.data.open_cdn_count) === 1 ? "" : "s"} AED{" "}
                        {formatAED(viewDialog.data.open_cdn_balance)}
                        {" · "}
                        {viewDialog.data.open_proforma_count} Proforma{Number(viewDialog.data.open_proforma_count) === 1 ? "" : "s"} AED{" "}
                        {formatAED(viewDialog.data.open_proforma_balance)}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: "#047857", mt: 0.25 }}>
                      No earlier balance owed — no unpaid invoices, CDNs or Proformas.
                    </Typography>
                  )}
                </Box>
                {Number(viewDialog.data.total_owed) > 0 && (
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() =>
                      setUnpaidDialog({
                        open: true,
                        customerId: viewDialog.data.customer_id,
                        excludeCdnId: viewDialog.data.doc_type === "cdn_convert" ? viewDialog.data.doc_id : null,
                      })
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 14,
                      borderRadius: "8px",
                      px: 2.5,
                      py: 1,
                      bgcolor: "#B45309",
                      "&:hover": { bgcolor: "#92400E" },
                      flexShrink: 0,
                    }}
                  >
                    View unpaid details
                  </Button>
                )}
              </Box>
              {viewDialog.data.admin_notes && (
                <Box sx={{ bgcolor: "#FDECEA", border: "1px solid #F5C2BD", borderRadius: "10px", px: 2, py: 1.25 }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, color: "#B42318", textTransform: "uppercase" }}>
                    Admin's note
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#7A271A", mt: 0.25 }}>{viewDialog.data.admin_notes}</Typography>
                </Box>
              )}
              {viewDialog.data.doc_number && (
                <Typography variant="body2">
                  {viewDialog.data.doc_type === "cdn_convert" ? "CDN: " : "Created: "}
                  <strong>{viewDialog.data.doc_number}</strong>
                  {viewDialog.data.doc_type === "cdn_convert" && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" "}— approving unlocks Credit when this CDN is converted to an
                      invoice; nothing is created now.
                    </Typography>
                  )}
                  {viewDialog.data.doc_pdf_url && (
                    <>
                      {" — "}
                      <Link href={viewDialog.data.doc_pdf_url} target="_blank" rel="noopener">
                        Open PDF
                      </Link>
                    </>
                  )}
                </Typography>
              )}

              {/* Document mockup — same layout/fields the actual PDF prints
                  (see generateOrderReceiptPdf / renderProformaPdf), built
                  straight from the parked form data so it matches exactly. */}
              {viewDialog.data.document && (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 2, pb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Box sx={{ textAlign: "right" }}>
                        {viewDialog.data.document.order_date && (
                          <Typography variant="caption" display="block">
                            <b>Date:</b> {dayjs(viewDialog.data.document.order_date).format("DD-MM-YYYY")}
                          </Typography>
                        )}
                        {viewDialog.data.document.store_name && (
                          <Typography variant="caption" display="block">
                            <b>Store:</b> {viewDialog.data.document.store_name}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block">
                          <b>Payment Method:</b> Credit
                        </Typography>
                        <Typography variant="caption" display="block">
                          <b>Payment Status:</b> Pending
                        </Typography>
                        {/* Days/due date use whatever's in the Approved Credit
                          Days field below, live — same as the create form,
                          which recalculates Due Date the moment Credit Days
                          changes. Falls back to what was requested until
                          Admin edits it. */}
                        <Typography variant="caption" display="block">
                          <b>Credit Days:</b>{" "}
                          {viewDialog.days || viewDialog.data.requested_days || "—"}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <b>Due Date:</b>{" "}
                          {(() => {
                            const days = Number(viewDialog.days || viewDialog.data.requested_days);
                            if (!days) return "—";
                            return dayjs(viewDialog.data.document.order_date || undefined)
                              .add(days, "day")
                              .format("DD-MM-YYYY")
                              .toUpperCase();
                          })()}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      align="center"
                      sx={{ fontWeight: 800, letterSpacing: 0.5, fontSize: 17, color: "text.primary", mt: 1 }}
                    >
                      {DOC_TITLES[viewDialog.data.doc_type] || "DOCUMENT"}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderBottomWidth: 2, borderColor: "text.primary" }} />

                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      <Box sx={{ flex: "1 1 200px" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Seller Details
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {DOC_SELLER.companyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {DOC_SELLER.addressLine}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {DOC_SELLER.cityEmirate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          TRN: {DOC_SELLER.trn}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: "1 1 200px" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Customer Details
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="body2" display="block">
                          {viewDialog.data.document.customer_name || viewDialog.data.customer_name || "NA"}
                        </Typography>
                        {viewDialog.data.document.customer_email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {viewDialog.data.document.customer_email}
                          </Typography>
                        )}
                        {viewDialog.data.document.customer_phone && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {viewDialog.data.document.customer_phone}
                          </Typography>
                        )}
                        {viewDialog.data.document.address && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {viewDialog.data.document.address}
                            {viewDialog.data.document.emirate ? `, ${viewDialog.data.document.emirate}` : ""}
                          </Typography>
                        )}
                        {viewDialog.data.document.vat_no && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            VAT No: {viewDialog.data.document.vat_no}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 16px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700 }}>
                          Product
                        </th>
                        <th style={{ padding: "8px 16px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                          Qty
                        </th>
                        <th style={{ padding: "8px 16px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                          Unit Price
                        </th>
                        <th style={{ padding: "8px 16px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                          Discount
                        </th>
                        <th style={{ padding: "8px 16px", borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewDialog.data.document.items.map((it, i) => (
                        <tr key={`${it.product_id}-${i}`}>
                          <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                            {it.product_name}
                            {it.sku && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                SKU: {it.sku}
                              </Typography>
                            )}
                          </td>
                          <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right" }}>
                            {it.quantity}
                          </td>
                          <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right" }}>
                            {formatAED(it.selling_price)}
                          </td>
                          <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right" }}>
                            {it.discount_amount > 0 ? `-${formatAED(it.discount_amount)}` : formatAED(0)}
                          </td>
                          <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                            {formatAED(it.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Box sx={{ p: 2 }}>
                    <Box sx={{ maxWidth: 260, ml: "auto" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                        <Typography variant="body2">{formatAED(viewDialog.data.document.subtotal)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Tax:</Typography>
                        <Typography variant="body2">+{formatAED(viewDialog.data.document.tax_amount)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                        <Typography variant="body2">{formatAED(viewDialog.data.document.shipping)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">Discount:</Typography>
                        <Typography variant="body2">-{formatAED(viewDialog.data.document.discount)}</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1.5,
                          pt: 1.5,
                          borderTop: "2px solid",
                          borderColor: "text.primary",
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>Grand Total:</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
                          {formatAED(viewDialog.data.document.grand_total)}
                        </Typography>
                      </Box>
                      {Number(viewDialog.data.document.advance_received) > 0 && (
                        <>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">Advance Received:</Typography>
                            <Typography variant="body2">
                              -{formatAED(viewDialog.data.document.advance_received)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography sx={{ fontWeight: 700 }}>Balance on Credit:</Typography>
                            <Typography sx={{ fontWeight: 700, color: "error.main" }}>
                              {formatAED(
                                Math.max(
                                  0,
                                  Number(viewDialog.data.document.grand_total || 0) -
                                    Number(viewDialog.data.document.advance_received || 0)
                                )
                              )}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

              {viewDialog.data.status === "Pending" && isAdmin && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <TextField
                    label="Approved Credit Days"
                    type="number"
                    size="small"
                    sx={{ maxWidth: 220 }}
                    value={viewDialog.days}
                    onChange={(e) => {
                      // Cap at 10 years — an unbounded value overflows dayjs
                      // Due Date math into "Invalid Date" elsewhere.
                      let v = e.target.value;
                      if (v && Number(v) > MAX_CREDIT_DAYS) v = String(MAX_CREDIT_DAYS);
                      setViewDialog((prev) => ({ ...prev, days: v }));
                    }}
                    inputProps={{ min: 1, max: MAX_CREDIT_DAYS }}
                    helperText="Approving creates this document on credit terms"
                  />
                  {/* Right next to the field itself (not just in the scrolled
                    document header above) so it's visible without scrolling
                    back up, and updates as the days above are edited. */}
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      Due Date
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {(() => {
                        const days = Number(viewDialog.days);
                        if (!days) return "—";
                        // CDN-conversion credit runs from the day it's converted.
                        const from =
                          viewDialog.data.doc_type === "cdn_convert"
                            ? undefined
                            : viewDialog.data.document?.order_date || undefined;
                        return dayjs(from)
                          .add(days, "day")
                          .format("DD-MM-YYYY");
                      })()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#f5f6f8" }}>
          <Button onClick={() => setViewDialog((prev) => ({ ...prev, open: false }))} color="inherit">
            Close
          </Button>
          {viewDialog.data?.status === "Pending" && isAdmin && (
            <>
              <Button
                color="error"
                variant="outlined"
                disabled={viewDialog.loading}
                onClick={() => {
                  const d = viewDialog.data;
                  setViewDialog((prev) => ({ ...prev, open: false }));
                  setRejectDialog({
                    open: true,
                    row: { ...d, customer_name: d.customer_name, requested_days: d.requested_days },
                    reason: "",
                    loading: false,
                  });
                }}
              >
                Reject
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={viewDialog.loading || !viewDialog.days || Number(viewDialog.days) <= 0}
                // CDN conversion credit creates/reserves nothing — no stock check.
                onClick={
                  viewDialog.data?.doc_type === "cdn_convert" ? handleViewApprove : openStockConfirm
                }
              >
                {viewDialog.data?.doc_type === "cdn_convert" ? "Approve Credit" : "Approve & Create"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Stock check before the document is actually created — same
          before/after reservation table the user's own create form shows,
          computed fresh so it reflects stock as of right now, not whenever
          the request was submitted. */}
      <Dialog
        open={stockConfirm.open}
        onClose={() => !stockConfirm.loading && setStockConfirm({ open: false, loading: false, impact: [] })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { position: "relative", borderRadius: "14px" } }}
      >
        {/* Creating: a clear in-progress state over the whole dialog while the
            document is created, stock reserved and the PDF generated. */}
        {stockConfirm.loading && stockConfirm.impact.length > 0 && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              bgcolor: "rgba(255,255,255,0.92)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              textAlign: "center",
              px: 4,
              borderRadius: "14px",
            }}
          >
            <CircularProgress size={46} thickness={4} />
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
              Creating {viewDialog.data?.document_label || DOC_LABELS[viewDialog.data?.doc_type] || "document"}…
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748B", maxWidth: 360 }}>
              Reserving stock, recording the advance and generating the PDF. This can take a few seconds — please
              keep this window open.
            </Typography>
          </Box>
        )}
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Stock Before Creating
        </DialogTitle>
        <DialogContent dividers>
          {stockConfirm.loading && stockConfirm.impact.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Approving will {viewDialog.data?.doc_type === "invoice" ? "deduct" : "reserve"} the
                stock below for <strong>{viewDialog.data?.document_label || DOC_LABELS[viewDialog.data?.doc_type]}</strong>{" "}
                {viewDialog.data?.company_name || viewDialog.data?.customer_name}. Review it before confirming.
              </Typography>
              <ReservationImpactTable rows={stockConfirm.impact} mode="create" />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setStockConfirm({ open: false, loading: false, impact: [] })}
            color="inherit"
            disabled={stockConfirm.loading}
          >
            Back
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={stockConfirm.loading}
            onClick={handleViewApprove}
          >
            {stockConfirm.loading ? (
              <>
                <CircularProgress size={16} thickness={5} sx={{ color: "inherit", mr: 1 }} />
                Creating…
              </>
            ) : (
              `Confirm & Create ${DOC_LABELS[viewDialog.data?.doc_type] || ""}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit trail: every step of one request, oldest first */}
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog((p) => ({ ...p, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Request #{historyDialog.row?.id} — History
          <Typography variant="caption" display="block" color="text.secondary">
            {historyDialog.row?.company_name || historyDialog.row?.customer_name}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historyDialog.loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : historyDialog.events.length === 0 ? (
            <Typography variant="body2">No history found.</Typography>
          ) : (
            <Box>
              {historyDialog.legacy && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  This request pre-dates the history log, so only its request and final decision are shown.
                </Typography>
              )}
              {historyDialog.events.map((ev, idx) => (
                <Box
                  key={`${ev.id}-${idx}`}
                  sx={{
                    pl: 2,
                    pb: 2,
                    ml: 0.5,
                    borderLeft: idx === historyDialog.events.length - 1 ? "2px solid transparent" : "2px solid #cbd5e1",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: -7,
                      top: 3,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: ev.action === "Approved" ? "#16a34a" : ev.action === "Rejected" ? "#dc2626" : "#1976d2",
                    }}
                  />
                  {historyDialog.events.some((e) => e.request_id && e.request_id !== historyDialog.events[0].request_id) &&
                    (idx === 0 || ev.request_id !== historyDialog.events[idx - 1]?.request_id) && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: "#64748B", textTransform: "uppercase", mb: 0.5 }}>
                        Request #{ev.request_id}
                        {ev.request_id === historyDialog.row?.id ? " (this request)" : ""}
                      </Typography>
                    )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      size="small"
                      label={ev.action}
                      color={ev.action === "Approved" ? "success" : ev.action === "Rejected" ? "error" : "primary"}
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatLocalDateTime(ev.created_at, "DD-MM-YYYY hh:mm A")}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    By <b>{ev.actor_name || "-"}</b>
                    {ev.requested_days != null && ev.action === "Requested" && <> · requested {ev.requested_days} days</>}
                    {ev.approved_days != null && ev.action === "Approved" && <> · approved {ev.approved_days} days</>}
                  </Typography>
                  {ev.doc_number && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Document: {ev.doc_number}
                    </Typography>
                  )}
                  {ev.note && (
                    <Typography variant="caption" sx={{ fontStyle: "italic", color: "#475569" }} display="block">
                      “{ev.note}”
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog((p) => ({ ...p, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
